import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import type { MemberModulePermissions } from "../../types";
import { supabase } from "./supabase";
import { resolveAvatarUrl } from "@/components/avatar/resolveAvatar";
import { resolveStorageDisplayUrl } from "@/lib/supabase/storageObjectUrl";
import type { ModuleId, OrgModulesState } from "@/modules/registry";
import {
  setCurrentWorkspace,
  type WorkspaceIdentity,
} from "@/lib/workspaceBranding";

const baseAuthProvider = supabaseAuthProvider(supabase, {
  getIdentity: async () => {
    const ownSale = await getSale(false);

    if (ownSale == null) {
      throw new Error();
    }

    // Platform operator viewing as a company: be that company's admin
    // (read-only), so every screen shows what they see — not LBS's user.
    const viewingAs = await fetchViewAsCompany();
    const sale =
      (viewingAs && (await fetchViewedCompanyAdmin(viewingAs.org_id))) ||
      ownSale;

    const [rawAvatar, orgModules, workspace] = [
      resolveAvatarUrl(sale as any, 96),
      await fetchOrgModules(),
      await fetchWorkspace(),
    ];
    const avatar =
      (await resolveStorageDisplayUrl(rawAvatar, {
        defaultBucket: "avatars",
      })) ?? rawAvatar;

    return {
      id: sale.id,
      org_id: sale.org_id ?? null,
      fullName: `${sale.first_name} ${sale.last_name}`,
      avatar,
      administrator: sale.administrator === true,
      role: sale.administrator ? "admin" : (sale.roles?.[0] ?? "user"),
      roles: sale.roles ?? (sale.administrator ? ["admin"] : []),
      org_modules: orgModules,
      workspace,
      viewing_as: viewingAs,
      module_permissions:
        (sale.module_permissions as
          | MemberModulePermissions
          | null
          | undefined) ?? null,
    };
  },
});

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data } = await supabase.from("init_state").select("is_initialized");
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

/**
 * The member's company: its name, and whether it is the platform's home org
 * (LBS), which keeps the original Sigma by LBS branding (Phase 4).
 */
const fetchWorkspace = async (): Promise<WorkspaceIdentity | null> => {
  const [{ data: org }, { data: homeOrgId }, { data: configRow }] =
    await Promise.all([
      supabase
        .from("organizations")
        .select("id, name, email, phone, address, website")
        .maybeSingle(),
      supabase.rpc("legacy_storage_org_id"),
      supabase.from("configuration").select("config").maybeSingle(),
    ]);
  if (!org) return null;
  const text = (value: unknown) => String(value ?? "").trim();
  const workspace = {
    id: Number(org.id),
    name: text(org.name),
    email: text(org.email),
    phone: text(org.phone),
    address: text(org.address),
    website: text(org.website),
    is_home: homeOrgId == null || Number(homeOrgId) === Number(org.id),
  };
  // Signatures, templates and invoice copy fall back to this company only.
  const logo = (configRow?.config as { lightModeLogo?: unknown } | null)
    ?.lightModeLogo;
  const logoSrc = String(
    (typeof logo === "string" ? logo : (logo as { src?: string })?.src) ?? "",
  ).trim();
  setCurrentWorkspace({
    // Only a public https logo shows in emails (not the default app mark).
    emailLogoUrl:
      /^https:\/\//i.test(logoSrc) && !/[?&]token=/.test(logoSrc)
        ? logoSrc
        : null,
    isHome: workspace.is_home,
    name: workspace.name,
    email: workspace.email,
    phone: workspace.phone,
    address: workspace.address,
    website: workspace.website,
  });
  return workspace;
};

/** The operator's active "View as company" session (null for everyone else). */
const fetchViewAsCompany = async (): Promise<{
  org_id: number;
  org_name: string;
} | null> => {
  const { data, error } = await supabase.rpc("platform_current_view_session");
  if (error) return null;
  const row = (data as Array<{ org_id: number; org_name: string }> | null)?.[0];
  return row ? { org_id: Number(row.org_id), org_name: row.org_name } : null;
};

/** First active admin of the viewed company (reads run as that company). */
const fetchViewedCompanyAdmin = async (orgId: number) => {
  const { data } = await supabase
    .from("organization_members")
    .select(
      "id, org_id, first_name, last_name, email, user_id, avatar, avatar_type, avatar_url, administrator, roles, module_permissions",
    )
    .eq("org_id", orgId)
    .eq("administrator", true)
    .eq("disabled", false)
    .order("id", { ascending: true })
    .limit(1)
    .maybeSingle();
  return data ?? null;
};

/** Modules the member's org has on (RLS returns only their org's rows). */
const fetchOrgModules = async (): Promise<OrgModulesState | null> => {
  const { data, error } = await supabase
    .from("organization_modules")
    .select("module_id, enabled, options");
  // Missing table / transient error: keep every module visible.
  if (error || !data) return null;
  const rows = data as Array<{
    module_id: ModuleId;
    enabled: boolean;
    options: Record<string, unknown> | null;
  }>;
  return {
    enabled: rows.filter((row) => row.enabled).map((row) => row.module_id),
    options: Object.fromEntries(
      rows.map((row) => [row.module_id, row.options ?? {}]),
    ),
  };
};

const fetchSale = async () => {
  const { data: dataSession, error: errorSession } =
    await supabase.auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await supabase
    .from("organization_members")
    .select(
      "id, org_id, first_name, last_name, email, user_id, avatar, avatar_type, avatar_url, administrator, roles, module_permissions",
    )
    .match({ user_id: dataSession?.session?.user.id })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  const storage = getLocalStorage();
  storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
  return dataSale;
};

const getSale = async (useCache = true) => {
  const storage = getLocalStorage();
  const cachedValue = useCache
    ? storage?.getItem(CURRENT_SALE_CACHE_KEY)
    : null;
  if (cachedValue != null) {
    return JSON.parse(cachedValue);
  }

  return fetchSale();
};

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
}

/**
 * When the app is deployed with a Vite `base` (e.g. /profile/), pathname is
 * /profile/sign-up, not /sign-up — strict equality in checkAuth was skipping
 * the public page and users ended up in the app with an active session.
 *
 * Client share links (invoice, subscription agreement, etc.) must also skip
 * auth — otherwise a fresh visitor hits “not initialized” → /login.
 */
function isPublicAuthRoute(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  const hash = window.location.hash;
  const segment = (s: "sign-up" | "set-password" | "forgot-password") =>
    path === `/${s}` || path.endsWith(`/${s}`) || hash.includes(`#/${s}`);
  if (segment("set-password") || segment("forgot-password")) {
    return true;
  }

  const publicRoots = [
    "/b",
    "/c",
    "/cal",
    "/f",
    "/pr",
    "/iv",
    "/p",
    "/book",
    "/forms",
    "/proposal",
    "/invoice",
    "/portal",
    "/sub",
    "/sub-agree",
  ];
  return publicRoots.some(
    (root) => path === root || path.startsWith(`${root}/`),
  );
}

export const authProvider: AuthProvider = {
  ...baseAuthProvider,
  checkError: async (error) => {
    // ra-supabase logs out on 403 too; RLS denials are not invalid sessions.
    if (error?.status === 403) {
      return;
    }

    if (error?.status === 400 && error?.name === "AuthSessionMissingError") {
      return Promise.reject();
    }

    if (error?.status === 401) {
      const { data } = await supabase.auth.getSession();
      if (data.session?.access_token) {
        return;
      }
      return Promise.reject();
    }

    return baseAuthProvider.checkError(error);
  },
  login: async (params) => {
    if (params.ssoDomain) {
      const { error } = await supabase.auth.signInWithSSO({
        domain: params.ssoDomain,
      });
      if (error) {
        throw error;
      }
      clearCache();
      await fetchSale();
      return;
    }
    const result = await baseAuthProvider.login(params);
    clearCache();
    await fetchSale();
    return result;
  },
  logout: async (params) => {
    clearCache();
    return baseAuthProvider.logout(params);
  },
  checkAuth: async (params) => {
    if (isPublicAuthRoute()) {
      return;
    }

    const isInitialized = await getIsInitialized();

    if (!isInitialized) {
      clearCache();
      await supabase.auth.signOut();
      throw {
        redirectTo: "/login",
        message: false,
      };
    }

    // Verify the user still has an organization_member row (catches deleted/disabled orgs)
    const sale = await getSale(false);
    if (sale == null) {
      clearCache();
      await supabase.auth.signOut();
      throw {
        redirectTo: "/login",
        message: "Your account no longer has access to this CRM.",
      };
    }

    return baseAuthProvider.checkAuth(params);
  },
  canAccess: async (params) => {
    const isInitialized = await getIsInitialized();
    if (!isInitialized) return false;

    // Get the current user
    const sale = await getSale();
    if (sale == null) return false;

    return canAccess(
      {
        administrator: sale.administrator,
        role: sale.administrator ? "admin" : "user",
        roles: sale.roles ?? [],
        module_permissions:
          (sale.module_permissions as
            | MemberModulePermissions
            | null
            | undefined) ?? null,
      },
      params,
    );
  },
  getAuthorizationDetails(authorizationId: string) {
    return supabase.auth.oauth.getAuthorizationDetails(authorizationId);
  },
  approveAuthorization(authorizationId: string) {
    return supabase.auth.oauth.approveAuthorization(authorizationId);
  },
  denyAuthorization(authorizationId: string) {
    return supabase.auth.oauth.denyAuthorization(authorizationId);
  },
};
